import { Fragment, useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNotification } from '../../contexts/NotificationContext';
import { api } from '../../utils/api';
import ProtectedRoute from '../../components/ProtectedRoute';
import AdminLayout, { useTheme, T } from '../../components/AdminLayout';
import { timeToMinutes, effectiveEndMinutes, isOvernight, wallMinutesToHHMM } from '../../utils/slotTime';

const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const DAYS_SHORT = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

function buildTimeOptions() {
  const opts = [];
  for (let h = 0; h < 24; h++) {
    for (const m of [0, 15, 30, 45]) {
      opts.push(`${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`);
    }
  }
  return opts;
}

function format12h(time24) {
  if (!time24) return '';
  const [h, m] = time24.split(':').map(Number);
  const ampm = h >= 12 ? 'PM' : 'AM';
  const h12 = h % 12 || 12;
  return `${h12}:${String(m).padStart(2, '0')} ${ampm}`;
}
const TIME_OPTIONS = buildTimeOptions();

/** Generate 15-min slot starts from startTime until endTime (exclusive). Overnight windows wrap past midnight. */
function generateSlots(startTime, endTime) {
  const effEnd = effectiveEndMinutes(startTime, endTime);
  let cur = timeToMinutes(startTime);
  const slots = [];
  while (cur < effEnd) {
    slots.push({
      time: wallMinutesToHHMM(cur),
      nextDay: cur >= 1440,
    });
    cur += 15;
  }
  return slots;
}

// ── Slot chip colours ────────────────────────────────────────────────────────
const chipStyle = (type) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '0.3rem',
  padding: '0.3rem 0.55rem',
  borderRadius: '6px',
  fontSize: '0.75rem',
  fontFamily: "'DM Sans', sans-serif",
  letterSpacing: '0.05em',
  cursor: 'pointer',
  border: 'none',
  transition: 'all 0.15s',
  background: type === 'break'
    ? 'rgba(251,191,36,0.18)'
    : type === 'emergency'
      ? 'rgba(239,68,68,0.18)'
      : 'rgba(255,255,255,0.05)',
  color: type === 'break'
    ? '#fbbf24'
    : type === 'emergency'
      ? '#ef4444'
      : 'var(--text-muted)',
  outline: type && type !== 'available' ? `1px solid ${type === 'break' ? '#fbbf24' : '#ef4444'}` : '1px solid rgba(255,255,255,0.08)',
});

const BLOCK_ICON = { available: '✅', break: '🛑', emergency: '⚡' };

// ── Slot Panel ──────────────────────────────────────────────────────────────
function SlotPanel({ config, onClose }) {
  const { t } = useTranslation();
  const { showAlert } = useNotification();
  const { T } = useTheme();
  const [blocked, setBlocked] = useState([]); // [{id, time, block_type}]
  const [loading, setLoading] = useState(true);

  const loadBlocked = useCallback(async () => {
    setLoading(true);
    try { setBlocked(await api.getBlockedSlots(config.id)); }
    catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [config.id]);

  useEffect(() => { loadBlocked(); }, [loadBlocked]);

  const slots = generateSlots(config.start_time, config.end_time);
  const overnight = isOvernight(config.start_time, config.end_time);

  const typeOf = (time) => {
    const b = blocked.find(b => b.time === time);
    return b ? b.block_type : 'available';
  };

  const toggle = async (time) => {
    const current = typeOf(time);
    const cycle = { available: 'break', break: 'emergency', emergency: 'available' };
    const next = cycle[current];
    try {
      if (next === 'available') {
        // delete the existing block
        const b = blocked.find(b => b.time === time);
        if (b) { await api.deleteBlockedSlot(b.id); }
      } else {
        await api.createBlockedSlot({ slot_config_id: config.id, time, block_type: next });
      }
      await loadBlocked();
    } catch (err) {
      showAlert(err?.message || t('admin.failedToUpdateBlockedSlot', 'Failed to update blocked slot'));
    }
  };

  return (
    <tr>
      <td colSpan={6} style={{ padding: 0 }}>
        <div style={{
          background: 'rgba(27,213,173,0.04)',
          borderTop: `1px solid rgba(27,213,173,0.15)`,
          borderBottom: `1px solid rgba(27,213,173,0.15)`,
          padding: '1.25rem 1.5rem',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <div>
              <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.85rem', color: T.orange, letterSpacing: '0.08em' }}>
                SLOTS — {format12h(config.start_time)} → {format12h(config.end_time)}
                {overnight ? ` (${t('admin.crossesMidnightHint')})` : ''}
              </span>
              <span style={{ marginLeft: '1rem', fontSize: '0.75rem', color: T.muted }}>
                {t('admin.slotCycleHint')}
              </span>
            </div>
            <button onClick={onClose} style={{ background: 'none', border: 'none', color: T.muted, cursor: 'pointer', fontSize: '1rem' }}>✕ {t('common.close')}</button>
          </div>
          {loading ? (
            <p style={{ color: T.muted, fontSize: '0.82rem' }}>{t('admin.loadingSlots')}</p>
          ) : (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
              {slots.map(({ time, nextDay }) => {
                const type = typeOf(time);
                const label = `${format12h(time)}${nextDay ? ` (${t('admin.crossesMidnightHint')})` : ''}`;
                return (
                  <button key={`${time}-${nextDay}`} style={chipStyle(type)} onClick={() => toggle(time)}
                    title={`${label} — ${type}`}>
                    {BLOCK_ICON[type]} {label}
                  </button>
                );
              })}
            </div>
          )}
          {/* Legend */}
          <div style={{ display: 'flex', gap: '1.5rem', marginTop: '1rem', fontSize: '0.75rem', color: T.muted }}>
            <span><span style={{ color: T.muted }}>✅</span> {t('admin.available')}</span>
            <span><span style={{ color: '#fbbf24' }}>🛑</span> <span style={{ color: '#fbbf24' }}>{t('admin.break')}</span></span>
            <span><span style={{ color: '#ef4444' }}>⚡</span> <span style={{ color: '#ef4444' }}>{t('admin.emergency')}</span></span>
          </div>
        </div>
      </td>
    </tr>
  );
}

// ── Main Page ────────────────────────────────────────────────────────────────
function TimeSlotsContent() {
  const { t } = useTranslation();
  const { showAlert, showConfirm } = useNotification();
  const [slots, setSlots] = useState([]);
  const [shops, setShops] = useState([]);
  const [allBranches, setAllBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [selectedShopId, setSelectedShopId] = useState('');
  const [form, setForm] = useState({ branch_id: '', day_of_week: 0, start_time: '09:00', end_time: '17:00', slot_duration_minutes: 15 });

  const filteredBranches = allBranches.filter(b => !selectedShopId || b.shop_id === parseInt(selectedShopId));

  const fetchData = async () => {
    try {
      const [slotData, shopData, branchData] = await Promise.all([
        api.getAdminTimeSlots(),
        api.getAdminShops(),
        api.getBranches(),
      ]);
      setSlots(slotData);
      setShops(shopData);
      setAllBranches(branchData);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, []);

  const branchById = (id) => allBranches.find(b => b.id === id);
  const shopOfBranch = (branchId) => {
    const b = branchById(branchId);
    return shops.find(s => s.id === b?.shop_id)?.name ?? '—';
  };

  const openCreate = () => {
    setEditing('create');
    setSelectedShopId('');
    setForm({ branch_id: '', day_of_week: 0, start_time: '09:00', end_time: '17:00', slot_duration_minutes: 15 });
  };
  const openEdit = (s) => {
    const branch = branchById(s.branch_id);
    setSelectedShopId(String(branch?.shop_id ?? ''));
    setEditing(s.id);
    const start = TIME_OPTIONS.includes(s.start_time) ? s.start_time : '09:00';
    const end = TIME_OPTIONS.includes(s.end_time) ? s.end_time : '17:00';
    setForm({ branch_id: s.branch_id, day_of_week: s.day_of_week, start_time: start, end_time: end, slot_duration_minutes: 15 });
  };

  const handleShopChange = (shopId) => {
    setSelectedShopId(shopId);
    setForm(f => ({ ...f, branch_id: '' }));
  };

  const save = async () => {
    if (!form.branch_id) { showAlert(t('admin.pleaseSelectBranch')); return; }
    if (hasInvalidWindow) { showAlert(t('admin.invalidTimeWindow')); return; }
    try {
      const payload = { ...form, branch_id: parseInt(form.branch_id), slot_duration_minutes: 15 };
      if (editing === 'create') await api.createAdminTimeSlot(payload);
      else await api.updateAdminTimeSlot(editing, { day_of_week: form.day_of_week, start_time: form.start_time, end_time: form.end_time, slot_duration_minutes: 15 });
      setEditing(null); fetchData();
    } catch (err) {
      const msg = String(err?.message || '');
      if (msg.toLowerCase().includes('invalid time window') || msg.toLowerCase().includes('cannot equal opening')) showAlert(t('admin.invalidTimeWindow'));
      else showAlert(err.message);
    }
  };

  const remove = async (id) => {
    showConfirm(t('admin.deleteSlotConfigConfirm'), async () => {
      try { await api.deleteAdminTimeSlot(id); if (expandedId === id) setExpandedId(null); fetchData(); }
      catch (err) { showAlert(err.message); }
    });
  };

  const toggleExpand = (id) => setExpandedId(prev => (prev === id ? null : id));

  const { T, S } = useTheme();
  const labelStyle = { display: 'block', fontSize: '0.75rem', color: T.muted, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '0.4rem' };
  const hasInvalidWindow = timeToMinutes(form.end_time) === timeToMinutes(form.start_time);

  return (
    <AdminLayout>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: '2rem' }}>
        <div>
          <h1 style={S.pageTitle}>{t('admin.timeSlots')}</h1>
          <div style={{ width: '40px', height: '2px', background: T.orange }} />
        </div>
        <button style={S.btnOrange} onClick={openCreate}
          onMouseEnter={e => e.target.style.background = T.orangeHov}
          onMouseLeave={e => e.target.style.background = T.orange}>+ {t('admin.addSlotConfig')}</button>
      </div>

      {/* Create / Edit modal */}
      {editing && (
        <div style={S.modal}>
          <div style={S.modalBox}>
            <h2 style={S.modalTitle}>{editing === 'create' ? t('admin.newSlotConfig') : t('admin.editSlotConfig')}</h2>

            <label style={labelStyle}>1. {t('admin.selectSalon')}</label>
            <select value={selectedShopId} onChange={e => handleShopChange(e.target.value)} style={S.select}>
              <option value="">{t('admin.chooseSalon')}</option>
              {shops.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>

            <label style={labelStyle}>2. {t('admin.selectBranch')}</label>
            <select value={form.branch_id} onChange={e => setForm({ ...form, branch_id: e.target.value })} style={S.select} disabled={!selectedShopId}>
              <option value="">{selectedShopId ? t('admin.chooseBranch') : t('admin.selectSalonFirst')}</option>
              {filteredBranches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>

            <label style={labelStyle}>{t('admin.dayOfWeek')}</label>
            <select value={form.day_of_week} onChange={e => setForm({ ...form, day_of_week: parseInt(e.target.value) })} style={S.select}>
              {DAYS.map((d, i) => <option key={i} value={i}>{d}</option>)}
            </select>

            <label style={labelStyle}>{t('admin.openingTime')}</label>
            <select value={form.start_time} onChange={e => setForm({ ...form, start_time: e.target.value })} style={S.select}>
              {TIME_OPTIONS.map(tm => <option key={tm} value={tm}>{format12h(tm)}</option>)}
            </select>

            <label style={labelStyle}>{t('admin.closingTime')}</label>
            <select value={form.end_time} onChange={e => setForm({ ...form, end_time: e.target.value })} style={S.select}>
              {TIME_OPTIONS.map(tm => <option key={tm} value={tm}>{format12h(tm)}</option>)}
            </select>
            {hasInvalidWindow && (
              <p style={{ fontSize: '0.8rem', color: '#ef4444', marginBottom: '0.8rem' }}>
                {t('admin.invalidTimeWindow')}
              </p>
            )}

            <p style={{ fontSize: '0.8rem', color: T.muted, marginBottom: '1rem' }}>{t('admin.slotDurationFixed')}</p>
            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <button onClick={save} style={{ ...S.btnOrange, flex: 1 }}>{t('common.save')}</button>
              <button onClick={() => setEditing(null)} style={S.btnOutline}>{t('common.cancel')}</button>
            </div>
          </div>
        </div>
      )}

      {loading ? <p style={{ color: T.muted }}>{t('common.loading')}</p> : (
        <div style={{ overflowX: 'auto' }}>
          <table style={S.table}>
            <thead>
              <tr>{['', t('admin.salon'), t('admin.branch'), t('admin.day'), t('admin.hours'), t('admin.actions')].map((h, i) => <th key={i} style={S.th}>{h}</th>)}</tr>
            </thead>
            <tbody>
              {slots.map(s => {
                const expanded = expandedId === s.id;
                return (
                  <Fragment key={s.id}>
                    <tr
                      onClick={() => toggleExpand(s.id)}
                      style={{ cursor: 'pointer' }}
                      onMouseEnter={e => e.currentTarget.style.background = 'rgba(27,213,173,0.06)'}
                      onMouseLeave={e => e.currentTarget.style.background = expanded ? 'rgba(27,213,173,0.04)' : 'transparent'}>

                      {/* Expand caret */}
                      <td style={{ ...S.td, width: '28px', color: T.orange, fontFamily: "'DM Sans', sans-serif", transition: 'transform 0.2s', transform: expanded ? 'rotate(90deg)' : 'none' }}>▸</td>

                      <td style={{ ...S.td, color: T.orange, fontFamily: "'DM Sans', sans-serif", fontSize: '0.82rem', letterSpacing: '0.05em' }}>{shopOfBranch(s.branch_id)}</td>
                      <td style={S.td}>{branchById(s.branch_id)?.name ?? s.branch_id}</td>
                      <td style={{ ...S.td, color: T.orange, fontFamily: "'DM Sans', sans-serif" }}>{DAYS_SHORT[s.day_of_week] ?? s.day_of_week}</td>

                      {/* Hours pill */}
                      <td style={S.td}>
                        <span style={{
                          background: 'rgba(27,213,173,0.12)',
                          border: '1px solid rgba(27,213,173,0.3)',
                          borderRadius: '20px',
                          padding: '0.2rem 0.75rem',
                          fontFamily: "'DM Sans', sans-serif",
                          fontSize: '0.82rem',
                          letterSpacing: '0.06em',
                          color: T.white,
                        }}>
                          {format12h(s.start_time)} → {format12h(s.end_time)}
                          {isOvernight(s.start_time, s.end_time) ? ` (${t('admin.crossesMidnightHint')})` : ''}
                        </span>
                      </td>

                      <td style={S.td} onClick={e => e.stopPropagation()}>
                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                          <button style={S.btnSmall} onClick={() => openEdit(s)}
                            onMouseEnter={e => e.target.style.color = T.white}
                            onMouseLeave={e => e.target.style.color = T.muted}>{t('common.edit')}</button>
                          <button style={S.btnDanger} onClick={() => remove(s.id)}>{t('common.delete')}</button>
                        </div>
                      </td>
                    </tr>

                    {/* Expanded slot panel */}
                    {expanded && (
                      <SlotPanel key={`panel-${s.id}`} config={s} onClose={() => setExpandedId(null)} />
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </AdminLayout>
  );
}

export default function TimeSlots() {
  return <ProtectedRoute requireSuperAdmin><TimeSlotsContent /></ProtectedRoute>;
}
